import Component from '@glimmer/component';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import ConfirmationDialog from 'tio-reimagined/components/confirmation-dialog';
import { PhWarning } from 'ember-phosphor-icons';
import type ImpersonationService from 'tio-employee/services/impersonation';
import type SessionContextService from 'tio-employee/services/session-context';
import type SessionService from 'tio-employee/services/session';
import type RouterService from '@ember/routing/router-service';
import type Owner from '@ember/owner';

export default class ImpersonationBanner extends Component {
  @service declare impersonation: ImpersonationService;
  @service declare sessionContext: SessionContextService;
  @service declare session: SessionService;
  @service declare router: RouterService;

  @tracked timeRemaining = '';
  private _timerId: ReturnType<typeof setInterval> | null = null;

  constructor(owner: Owner, args: Record<string, never>) {
    super(owner, args);
    if (this.impersonation.isImpersonated) {
      this._tick();
      this._timerId = setInterval(() => this._tick(), 1000);
    }
  }

  willDestroy() {
    super.willDestroy();
    if (this._timerId) {
      clearInterval(this._timerId);
    }
  }

  private _tick() {
    const expireAt = this.session.expireAt;
    if (!expireAt) {
      this.timeRemaining = '';
      return;
    }
    const diff = Math.max(0, Math.floor((expireAt - Date.now()) / 1000));
    const mins = Math.floor(diff / 60);
    const secs = diff % 60;
    this.timeRemaining = `${mins}:${String(secs).padStart(2, '0')}`;
  }

  get personName() {
    return this.sessionContext.person?.fullName || 'this user';
  }

  endSession = () => {
    this.router.transitionTo('logout');
  };

  <template>
    {{#if this.impersonation.isImpersonated}}
      <div
        class="sticky top-0 z-[60] tio-bg-warning-soft border-b tio-border-warning px-4 py-2 tio-row text-sm tio-fg-warning"
        role="alert"
        data-test-impersonation-banner
      >
        <PhWarning @size={{18}} @weight="fill" class="tio-fg-warning shrink-0" />
        <span class="flex-1">
          {{t
            "impersonation.banner_message"
            personName=this.personName
            impersonatorId=this.impersonation.impersonatorPersonId
          }}
        </span>
        {{#if this.timeRemaining}}
          <span
            class="font-mono text-xs font-semibold tio-fg-warning tio-bg-warning-soft px-2 py-0.5 rounded"
            data-test-impersonation-countdown
          >
            {{this.timeRemaining}}
          </span>
        {{/if}}
        <button
          type="button"
          class="px-3 py-1 text-xs font-medium tio-fg-danger-body tio-bg-danger-soft border tio-border-danger rounded-md hover:tio-bg-danger-soft-hover transition-colors"
          {{on "click" this.endSession}}
          data-test-impersonation-end-session
        >
          {{t "impersonation.end_session"}}
        </button>
      </div>

      <ConfirmationDialog
        @isOpen={{this.impersonation.showMutationConfirm}}
        @title={{t "impersonation.confirm_mutation_title"}}
        @intent="warning"
        @confirmText={{t "impersonation.confirm_proceed"}}
        @cancelText={{t "impersonation.confirm_cancel"}}
        @onConfirm={{this.impersonation.handleConfirm}}
        @onCancel={{this.impersonation.handleCancel}}
      >
        {{t "impersonation.confirm_mutation_body"}}
      </ConfirmationDialog>
    {{/if}}
  </template>
}
